import { DynamicDrawUsage, Group, InstancedMesh, Matrix4, Mesh, Quaternion, Vector3 } from 'three';
import cartAsset from '../../data/cart.generated.json';
import type { BuildContext, FeatureOutput, Obstacle } from '../types';
import { emptyFeatureOutput } from '../types';
import { createMeshBuilder } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { beamSolid, boxSolid } from '../fabric/solids';
import { CARGO, cargoX } from '../life/cargoPlan';
import { citizenGeometry } from '../life/citizens/citizenShapes';
import { createCitizenBatch } from '../life/citizens/citizenBatch';
import { clipPoseAt, walkPose } from '../life/citizens/citizenPose';
import { streetAssetGeometry, streetInstances } from '../life/streetAsset';
import { buildBoatBody, buildHoldMask } from './openBoat';
import { SHIP_TEMPLATES } from './shipTemplates';
import type { PlacedShip } from './shipPlacement';
import { planHarbourTrade } from './tradePlan';
import { tradeEase, tradeTimetable } from './tradeMotion';
import { tradeFrames, tradeLines } from './tradeFrames';
import { buildTradeScale } from './tradeScale';
import { shipColors } from './shipColors';

/** One physical delivery links the anchored fleet to the old Dam Waag. Rates and apparatus are estimates. */
export function buildHarbourTrade(
  ctx: BuildContext,
  ships: readonly PlacedShip[],
  barriers: readonly Obstacle[]
): FeatureOutput {
  const asset = ctx.citizens;
  if (!asset) return emptyFeatureOutput();
  const plan = planHarbourTrade(ctx, ships, barriers);
  if (!plan) {
    ctx.warn('No clear lighter and handcart delivery to the Dam Waag');
    return emptyFeatureOutput();
  }
  const figures = ['carter', 'sailor', 'labourer', 'burgher'].map((trade) =>
    asset.figures.find((f) => f.trade === trade)
  );
  if (figures.some((f) => !f))
    throw new Error('Harbour trade requires the baked carter, sailor, labourer and burgher');
  ctx.materials.setCitizenPoses(asset.poses, asset.bones.length);
  const colors = shipColors(ctx.palette),
    material = ctx.materials.painted();
  const root = new Group();
  root.name = 'harbour-trade';
  const lighter = new Group();
  lighter.name = 'working-lighter';
  const hullBuilder = createMeshBuilder(3072);
  buildBoatBody(
    hullBuilder,
    SHIP_TEMPLATES.praam,
    'near',
    {
      hull: colors.hull,
      wale: colors.wale,
      rail: colors.rail,
      port: colors.port,
      rope: colors.rigging,
    },
    892,
    0
  );
  const hull = new Mesh(hullBuilder.finish(), material);
  hull.name = 'working-lighter-hull';
  lighter.add(hull);
  const maskBuilder = createMeshBuilder(64);
  buildHoldMask(maskBuilder, SHIP_TEMPLATES.praam, {
    color: colors.hull,
    surface: SURFACE.wood,
    edgeId: 892,
    strokeAngle: 0,
    extent: [1, 1],
  });
  const mask = new Mesh(maskBuilder.finish(), ctx.materials.painted({ depthOnly: true }));
  mask.name = 'working-lighter-hold-mask';
  lighter.add(mask);
  const cartGeometry = streetAssetGeometry(cartAsset.body, SURFACE.wood);
  const body = new Mesh(cartGeometry, material);
  body.name = 'trade-handcart';
  body.matrixAutoUpdate = false;
  const wheelGeometry = streetAssetGeometry(cartAsset.wheel, SURFACE.wood);
  streetInstances(wheelGeometry, 2, 893);
  const wheels = new InstancedMesh(wheelGeometry, ctx.materials.painted({ instanced: true }), 2);
  wheels.name = 'trade-cart-wheels';
  wheels.frustumCulled = false;
  wheels.instanceMatrix.setUsage(DynamicDrawUsage);
  const cargoGeometry = streetAssetGeometry(cartAsset.barrels, SURFACE.wood);
  cargoGeometry.translate(-1.23, -0.7, 0);
  const cargo = new Mesh(cargoGeometry, material);
  cargo.name = 'trade-cargo';
  const station = buildTradeScale(material, colors.hull, colors.rigging, colors.sail);
  const batch = createCitizenBatch(
    material,
    figures.map((f) => citizenGeometry(asset, f!.lods[0]!)),
    5,
    'trade-workers'
  );
  const lines = tradeLines(plan, ctx.site);
  const timetable = tradeTimetable(lines.water.length, lines.cart.length, plan.arrival);
  const frames = tradeFrames(plan, lines, timetable, ctx.site);
  station.group.matrixAutoUpdate = false;
  station.group.matrix.copy(frames.scaleFrame);
  const tackleBuilder = createMeshBuilder(256);
  const style = {
    color: colors.hull,
    surface: SURFACE.wood,
    edgeId: 894,
    strokeAngle: 0,
    extent: [1, 1] as const,
  };
  const quay = plan.quay,
    berth = plan.water.at(-1)!;
  const dx = berth[0] - quay[0],
    dy = berth[1] - quay[1],
    length = Math.hypot(dx, dy);
  const mast = new Vector3(
    quay[0] - (dx / length) * 0.6,
    ctx.site.queries.groundHeightAt(quay),
    -quay[1] + (dy / length) * 0.6
  );
  const jib = new Vector3(quay[0] + dx * 0.55, mast.y + 4, -quay[1] - dy * 0.55);
  const frame = { origin: [0, 0, 0] as const, yaw: 0 };
  beamSolid(tackleBuilder, style, frame, mast.toArray(), [mast.x, mast.y + 5, mast.z], 0.11);
  beamSolid(tackleBuilder, style, frame, [mast.x, mast.y + 4, mast.z], jib.toArray(), 0.1);
  beamSolid(tackleBuilder, style, frame, [mast.x, mast.y + 1.8, mast.z], jib.toArray(), 0.065);
  const tackle = new Mesh(tackleBuilder.finish(), material);
  tackle.name = 'quay-cargo-tackle';
  const shipTackle = createMeshBuilder(128);
  const sourceTop = Math.max(frames.sea[1] + 2.5, 3);
  beamSolid(
    shipTackle,
    style,
    frame,
    [plan.ship.position[0], sourceTop, -plan.ship.position[1]],
    [frames.sea[0], sourceTop, frames.sea[2]],
    0.1
  );
  beamSolid(
    shipTackle,
    style,
    frame,
    [plan.ship.position[0], frames.sea[1] + 0.2, -plan.ship.position[1]],
    [frames.sea[0], sourceTop, frames.sea[2]],
    0.065
  );
  const shipBoom = new Mesh(shipTackle.finish(), material);
  shipBoom.name = 'ship-cargo-boom';
  const ropeBuilder = createMeshBuilder(64);
  beamSolid(ropeBuilder, { ...style, color: colors.rigging }, frame, [0, 0, 0], [0, 1, 0], 0.014);
  const rope = new Mesh(ropeBuilder.finish(), material);
  rope.name = 'cargo-hoist-rope';
  rope.matrixAutoUpdate = false;
  const storeBuilder = createMeshBuilder(256);
  const storeFrame = { origin: frames.stored, yaw: plan.heading };
  boxSolid(
    storeBuilder,
    { ...style, color: colors.sail, surface: SURFACE.cloth },
    storeFrame,
    [-0.65, 0.65, -0.42],
    [0.65, 0.7, 0.42]
  );
  for (const x of [-0.65, 0.65])
    boxSolid(storeBuilder, style, storeFrame, [x - 0.04, -0.05, -0.42], [x + 0.04, 0.65, -0.34]);
  const store = new Mesh(storeBuilder.finish(), material);
  store.name = 'weighed-goods-cover';
  const obstacles: Obstacle[] = [];
  const post = new Vector3();
  for (const x of [-2.15, 2.15])
    for (const z of [-1.15, 1.15]) {
      const polygon = [
        [-0.15, -0.15],
        [0.15, -0.15],
        [0.15, 0.15],
        [-0.15, 0.15],
      ].map(([u, v]) => {
        post.set(x + u!, 0, z + v!).applyMatrix4(frames.scaleFrame);
        return [post.x, -post.z] as const;
      });
      obstacles.push({ id: `dam-weighing-post-${obstacles.length}`, kind: 'structure', polygon });
    }
  obstacles.push({
    id: 'quay-cargo-tackle',
    kind: 'structure',
    polygon: [
      [-0.15, -0.15],
      [0.15, -0.15],
      [0.15, 0.15],
      [-0.15, 0.15],
    ].map(([x, y]) => [mast.x + x!, -mast.z + y!] as const),
  });
  root.add(lighter, body, wheels, cargo, station.group, batch.mesh, tackle, shipBoom, rope, store);
  root.traverse((object) => {
    if (object instanceof Mesh) {
      object.frustumCulled = false;
      object.renderOrder = -2;
    }
  });
  mask.renderOrder = -1;
  const offset = timetable.starts.weigh + 2;
  const state = { plan, timetable, offset, stage: 'weigh', owner: 'scale', active: true };
  root.userData['trade'] = state;
  const local = new Matrix4(),
    worker = new Matrix4(),
    wheel = new Matrix4();
  const top = new Vector3(),
    bottom = new Vector3(),
    direction = new Vector3();
  const turn = new Quaternion(),
    up = new Vector3(0, 1, 0),
    ropeScale = new Vector3();
  let day = true,
    nightEnd: number | null = null,
    previousSeconds = 0,
    elapsed = offset;
  const animated = {
    id: 'linked-harbour-trade',
    setTimeOfDay(_minutes: number, activity: number) {
      day = activity > 0.05;
      if (day) nightEnd = null;
    },
    update(seconds: number) {
      if (seconds < previousSeconds) {
        elapsed = seconds + offset;
        nightEnd = null;
      } else {
        const next = elapsed + seconds - previousSeconds;
        if (!day && nightEnd === null)
          nightEnd = (Math.floor(elapsed / timetable.cycle) + 1) * timetable.cycle;
        elapsed = nightEnd === null ? next : Math.min(next, nightEnd);
      }
      previousSeconds = seconds;
      const motion = frames.at(elapsed);
      state.stage = motion.stage;
      state.owner = motion.owner;
      state.active = nightEnd === null || elapsed < nightEnd;
      lighter.matrixAutoUpdate = false;
      lighter.matrix.copy(motion.boat);
      body.matrix.copy(motion.body);
      const axle = cargoX(CARGO.axleX, 0.82);
      for (const side of [-1, 1]) {
        wheel
          .makeRotationZ(-motion.cartDistance / CARGO.wheelRadius)
          .setPosition(axle, CARGO.wheelRadius, side * 0.52)
          .premultiply(motion.cart);
        wheels.setMatrixAt((side + 1) / 2, wheel);
      }
      wheels.instanceMatrix.needsUpdate = true;
      cargo.visible = state.active;
      cargo.position.set(...motion.cargo);
      cargo.rotation.y = motion.cargoHeading;
      station.update(
        motion.scaleAngle,
        motion.stage === 'weigh'
          ? tradeEase((motion.progress - 0.12) / 0.65)
          : motion.stage === 'store'
            ? 1 - tradeEase((motion.progress - 0.7) / 0.3)
            : 0
      );
      batch.begin();
      const idle = clipPoseAt(asset.clips['Idle']!, seconds);
      const push = walkPose(asset.clips['PushCart']!, motion.cartDistance, 1);
      batch.draw(
        0,
        motion.cart,
        clipPoseAt(asset.clips['IdleCart']!, seconds),
        push,
        tradeEase(motion.cartSpeed / 0.85)
      );
      local.makeTranslation(2.25, frames.floor, 0).premultiply(motion.boat);
      batch.draw(
        1,
        local,
        idle,
        clipPoseAt(asset.clips['Haul']!, seconds),
        state.active && (motion.stage === 'load' || motion.stage === 'unload') ? 1 : 0
      );
      worker
        .makeRotationY(Math.atan2(berth[1] - quay[1], berth[0] - quay[0]))
        .setPosition(quay[0] - (dy / length) * 1.5, mast.y, -quay[1] - (dx / length) * 1.5);
      batch.draw(
        2,
        worker,
        idle,
        clipPoseAt(asset.clips['Haul']!, seconds),
        state.active && motion.stage === 'unload' ? 1 : 0
      );
      local
        .makeRotationY(-Math.PI / 2)
        .setPosition(-0.45, 0, 1)
        .premultiply(frames.scaleFrame);
      batch.draw(
        3,
        local,
        idle,
        clipPoseAt(asset.clips['Sell']!, seconds),
        state.active && motion.stage === 'weigh' ? 1 : 0
      );
      local
        .makeRotationY(-Math.PI / 2)
        .setPosition(-0.7, frames.sea[1], 0)
        .premultiply(frames.shipMatrix);
      batch.draw(
        2,
        local,
        idle,
        clipPoseAt(asset.clips['Haul']!, seconds),
        state.active && motion.stage === 'load' ? 1 : 0
      );
      batch.end();
      rope.visible = state.active && ['load', 'unload', 'lift', 'store'].includes(motion.stage);
      if (rope.visible) {
        if (motion.stage === 'load')
          top.set(frames.sea[0], Math.max(frames.sea[1] + 2.5, 3), frames.sea[2]);
        else if (motion.stage === 'unload') top.copy(jib);
        else top.set(0, 3.35, 0).applyMatrix4(frames.scaleFrame);
        bottom.set(...motion.cargo);
        bottom.y += 0.42;
        direction.copy(top).sub(bottom);
        const span = direction.length();
        turn.setFromUnitVectors(up, direction.normalize());
        rope.matrix
          .makeRotationFromQuaternion(turn)
          .scale(ropeScale.set(1, span, 1))
          .setPosition(bottom);
      }
    },
  };
  animated.update(0);
  return {
    objects: [root],
    animated: [animated],
    obstacles,
    counts: {
      workingLighters: 1,
      linkedCargoCarts: 1,
      weighingStations: 1,
      harbourTradeWorkers: 5,
    },
  };
}
